<template>
  <Card
    class="conversation-card"
    @click="$emit('click', conversation)"
  >
    <template #content>
      <div class="card-body">
        <div class="accent-bar" />
        <div class="card-inner">
          <div class="card-header-row">
            <span class="scenario-title">{{ conversation.scenario_title }}</span>
            <Tag
              :value="conversation.level"
              :severity="levelSeverity"
              class="level-tag"
            />
          </div>
          <div class="character-row">
            <i class="pi pi-user character-icon" />
            <span class="character-name">{{ conversation.character_name }}</span>
          </div>
          <p class="summary-text">
            {{ conversation.description || 'No description available.' }}
          </p>
          <div class="card-footer-row">
            <div class="footer-left">
              <i class="pi pi-calendar footer-icon" />
              <span class="conversation-date">{{ formattedDate }}</span>
            </div>
            <div class="footer-right">
              <Button
                class="delete-btn"
                icon="pi pi-trash"
                severity="danger"
                text
                rounded
                size="small"
                aria-label="Delete conversation"
                data-test="delete-conversation-btn"
                @click.stop="deleteDialogOpen = true"
              />
              <div class="footer-action">
                <span class="open-label">Open</span>
                <i class="pi pi-arrow-right footer-chevron" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Card>
  <Dialog
    v-model:visible="deleteDialogOpen"
    modal
    :header="CHAT_CONVERSATION_DELETE_TITLE"
    :style="{ width: '400px' }"
  >
    <p class="delete-warning">
      {{ CHAT_CONVERSATION_DELETE_WARNING_MSG }}
    </p>
    <p
      v-if="deleteError"
      class="delete-error"
    >
      {{ deleteError }}
    </p>
    <template #footer>
      <Button
        label="Cancel"
        text
        :disabled="isDeleting"
        @click="deleteDialogOpen = false"
      />
      <Button
        label="Delete"
        severity="danger"
        :loading="isDeleting"
        data-test="confirm-delete-conversation-btn"
        @click="confirmDelete"
      />
    </template>
  </Dialog>
</template>

<script>
import Card from 'primevue/card'
import Tag from 'primevue/tag'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import { deleteConversation } from '@/services/chatService'
import {
  CHAT_CONVERSATION_LEVEL_TAG_COLOR_MAP,
  CHAT_CONVERSATION_DELETE_TITLE,
  CHAT_CONVERSATION_DELETE_WARNING_MSG,
  CHAT_CONVERSATION_DELETE_ERROR_MSG
} from '@/assets/constants/constants'

export default {
  name: 'ConversationCard',

  components: { Card, Tag, Button, Dialog },

  props: {
    conversation: {
      type: Object,
      required: true
    }
  },

  emits: ['click', 'deleted'],

  data() {
    return {
      deleteDialogOpen: false,
      isDeleting: false,
      deleteError: '',
      CHAT_CONVERSATION_DELETE_TITLE,
      CHAT_CONVERSATION_DELETE_WARNING_MSG
    }
  },

  computed: {
    levelSeverity() {
      return CHAT_CONVERSATION_LEVEL_TAG_COLOR_MAP[this.conversation.level] || 'secondary'
    },
    formattedDate() {
      if (!this.conversation.created_at) return ''
      return new Date(this.conversation.created_at).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }
  },

  methods: {
    async confirmDelete() {
      this.isDeleting = true
      this.deleteError = ''
      try {
        await deleteConversation(this.conversation.session_id)
        this.deleteDialogOpen = false
        this.$emit('deleted', this.conversation.id)
      } catch (error) {
        this.deleteError = CHAT_CONVERSATION_DELETE_ERROR_MSG
      } finally {
        this.isDeleting = false
      }
    }
  }
}
</script>

<style scoped>
.conversation-card {
  width: 100% !important;
  cursor: pointer;
  background: var(--color-surface) !important;
  border-radius: var(--radius-lg) !important;
  border: 1px solid rgba(27,30,100,0.10);
  box-shadow: none !important;
  transition: transform var(--transition-fast), border-color var(--transition-base);
  overflow: hidden;
}
.conversation-card:deep(.p-card-body) {
  padding: 0 !important;
  width: 100% !important;
}

.conversation-card:hover {
  transform: translateY(-2px);
  border-color: var(--color-primary-mid) !important;
}

.conversation-card:hover .accent-bar {
  background: var(--color-primary-mid);
}

.conversation-card:hover .footer-action {
  color: var(--color-primary-mid);
}

.card-body {
  display: flex;
  flex-direction: row;
  background-color: var(--color-surface) !important;
  width: 100% !important;
}

.accent-bar {
  width: 5px;
  flex-shrink: 0;
  background: rgba(69,74,170,0.25);
  transition: background var(--transition-base);
}

.card-inner {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  width: 100% !important;
}


.card-header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.scenario-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
  flex: 1;
  line-height: var(--line-height-base);
}

.level-tag {
  flex-shrink: 0;
  font-size: 0.75rem;
}

.character-row {
  display: flex;
  align-items: center;
  gap: 5px;
}

.character-icon {
  font-size: 0.8rem;
  color: var(--color-primary-mid);
}

.character-name {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-primary-mid);
}

.summary-text {
  font-size: var(--font-size-base);
  color: var(--color-text-secondary);
  margin: 0;
  line-height: var(--line-height-base);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-footer-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: var(--space-2);
  margin-top: var(--space-1);
  border-top: 1px solid rgba(27,30,100,0.08);
}

.footer-left {
  display: flex;
  align-items: center;
  gap: 5px;
}

.footer-icon {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.conversation-date {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.footer-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.delete-btn {
  opacity: 0;
  transition: opacity 0.15s ease;
}

.conversation-card:hover .delete-btn,
.delete-btn:focus-visible {
  opacity: 1;
}

@media (hover: none) {
  .delete-btn {
    opacity: 1;
  }
}

.delete-warning {
  margin: 0;
  color: var(--color-text-secondary);
  line-height: var(--line-height-base);
}

.delete-error {
  margin: var(--space-3) 0 0;
  color: var(--color-error, #dc2626);
}

.footer-action {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #a5b4fc;
  transition: color 0.2s ease;
}

.footer-chevron {
  font-size: 0.7rem;
}
</style>
